/**
 * Live workflow-event subscription for an agent run, over the platform-api SSE
 * relay. Frames are WorkflowEvent envelopes, plus stream_end and error.
 *
 * `since_sequence` is an exclusive resume cursor: reconnect with the last
 * observed sequence and the gateway backfills without duplicates.
 */

import { getFreshToken } from '@/utils/auth-token-provider';
import * as Sentry from '@sentry/react';

export interface AgentWorkflowEventNode {
  node_id: string;
  node_type?: string | null;
  display_name?: string | null;
}

export interface AgentWorkflowEvent {
  event_id?: string;
  event_type: string;
  timestamp?: string;
  sequence?: number;
  node?: AgentWorkflowEventNode | null;
  legacy_type?: string | null;
  data: Record<string, unknown>;
}

export const COT_EVENT_TYPES = [
  'node.thinking',
  'node.agent_message',
  'node.tool_call',
  'node.tool_result',
  'node.node_failed',
  'unknown',
];

/**
 * What a run progress card subscribes to: run state transitions, node
 * start/finish, input requests, and tool calls (which name the tool for the
 * status line). Left out are `node.thinking` and `node.agent_message`, which
 * arrive one frame per streamed token chunk and make up nearly all of a
 * run's event log, and `node.tool_result`, which carries the full tool
 * payload. Not the complement of `COT_EVENT_TYPES`: the two lists overlap on
 * `node.tool_call` and `node.node_failed`. The gateway applies the filter
 * before it emits, so a subscriber that only wants progress also spares the
 * backend the replay.
 */
export const RUN_LIFECYCLE_EVENT_TYPES = [
  'workflow.run_started',
  'workflow.run_paused',
  'workflow.run_resumed',
  'workflow.run_completed',
  'workflow.run_failed',
  'node.node_started',
  'node.node_completed',
  'node.node_failed',
  'node.input_required',
  'node.tool_call',
];

// The label must not be preceded by `source_`, or the match runs inside
// `source_run_id` -- the run that originally produced an artifact. A rerun
// result carries `source_run_id` ahead of its own `run_id`, so without this
// exclusion the older run wins. Scoped to that one prefix rather than any
// word character so it doesn't also reject accepted spellings like
// `workflow_run_id` (see chat-stream.ts's done frame and stored message
// metadata, which use exactly that key for this same id).
const RUN_ID_RE =
  /(?<!source_)run[\s_-]?id\W{0,5}([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * Orchestrator tools that START (or re-start) a workflow run.
 *
 * Mirrors the orchestrator's own `_RUN_INITIATING_TOOLS`. Inspection tools
 * (`get_workflow_run_status`, `get_artifact_provenance`, ...) also echo a
 * run id, but for a run the turn merely looked at — attaching a card to
 * those makes the chat claim it launched something it did not.
 */
export const RUN_INITIATING_TOOLS: ReadonlySet<string> = new Set([
  'start_workflow',
  'recover_failed_run',
  'rerun_from_node',
  'rerun_step_in_context',
  'rerun_workflow',
]);

export function isRunInitiatingTool(toolName: string | undefined): boolean {
  return !!toolName && RUN_INITIATING_TOOLS.has(toolName);
}

/**
 * Pulls a run_id out of an orchestrator tool_result frame's rendered
 * content. Run-initiating tools surface the started run's id in their
 * result, but the rendering varies: JSON (`"run_id": "..."`), dict-style
 * (`'run_id': '...'`), or prose ("Run ID: `...` — status: queued") — so
 * the label match is deliberately loose around separators and quoting.
 *
 * Only meaningful for a tool in {@link RUN_INITIATING_TOOLS}; callers must
 * check the tool name first.
 */
export function extractRunIdFromText(text: string): string | null {
  const match = RUN_ID_RE.exec(text);
  return match ? match[1] : null;
}

export interface StreamAgentRunEventsOptions {
  orgId: string;
  agentId: string;
  runId: string;
  sinceSequence?: number;
  types?: string[];
  signal?: AbortSignal;
  onEvent: (event: AgentWorkflowEvent) => void;
  /** Run reached a terminal task state — no reconnect needed. */
  onEnd: (state: string | null) => void;
  /** Stream failed or dropped without a terminal frame. */
  onError: (error: string) => void;
}

const READ_TIMEOUT_MS = 45_000;

export async function streamAgentRunEvents(
  opts: StreamAgentRunEventsOptions,
): Promise<void> {
  const {
    orgId,
    agentId,
    runId,
    sinceSequence = 0,
    types,
    signal,
    onEvent,
    onEnd,
    onError,
  } = opts;

  const platformToken = await getFreshToken();
  if (!platformToken) {
    onError('Not authenticated');
    return;
  }

  let env = 'prod';
  if (typeof window !== 'undefined' && window.location) {
    const searchParams = new URLSearchParams(window.location.search);
    env = searchParams.get('env')?.toLowerCase() || 'prod';
  }

  const params = new URLSearchParams({
    env,
    since_sequence: String(sinceSequence),
  });
  if (types?.length) params.set('types', types.join(','));

  const url = `/proxy/platform/agents/${encodeURIComponent(orgId)}/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/events/stream?${params}`;

  try {
    const response = await fetch(url, {
      headers: { 'X-User-Token': platformToken },
      signal,
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      onError(
        `Events subscription failed: ${response.status} ${errText.slice(0, 200)}`,
      );
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError('Streaming not supported in this browser');
      return;
    }

    const decoder = new TextDecoder();
    let buf = '';
    let terminal = false;

    /** Returns true when a terminal frame (stream_end/error) was handled. */
    const handleLine = (line: string): boolean => {
      if (!line.startsWith('data: ')) return false;
      const raw = line.slice(6).trim();
      if (!raw) return false;

      let d: Record<string, unknown>;
      try {
        d = JSON.parse(raw);
      } catch {
        return false;
      }

      if (d.type === 'stream_end') {
        onEnd(typeof d.state === 'string' ? d.state : null);
        return true;
      }
      if (d.type === 'error') {
        onError((d.error as string) || 'Events stream failed');
        return true;
      }
      if (typeof d.event_type === 'string') {
        onEvent({
          event_id: typeof d.event_id === 'string' ? d.event_id : undefined,
          event_type: d.event_type,
          timestamp: typeof d.timestamp === 'string' ? d.timestamp : undefined,
          sequence: typeof d.sequence === 'number' ? d.sequence : undefined,
          node: (d.node ?? null) as AgentWorkflowEventNode | null,
          legacy_type: typeof d.legacy_type === 'string' ? d.legacy_type : null,
          data:
            d.data && typeof d.data === 'object'
              ? (d.data as Record<string, unknown>)
              : {},
        });
      }
      return false;
    };

    while (!terminal) {
      const readPromise = reader.read();
      const timeoutPromise = new Promise<'timeout'>((resolve) =>
        setTimeout(() => resolve('timeout'), READ_TIMEOUT_MS),
      );
      const result = await Promise.race([readPromise, timeoutPromise]);

      if (result === 'timeout') {
        reader.cancel().catch(() => undefined);
        onError('Events stream timed out');
        return;
      }

      const { done, value } = result;
      if (done) break;

      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith(':')) continue;
        if (handleLine(line)) {
          terminal = true;
          break;
        }
      }
    }

    if (!terminal) {
      onError('Events stream ended unexpectedly');
    }
  } catch (err) {
    if (signal?.aborted) return;
    Sentry.captureException(err, {
      extra: { context: 'stream-agent-run-events', orgId, agentId, runId },
    });
    onError(err instanceof Error ? err.message : String(err));
  }
}
